import Image from 'next/image';
import React, { Suspense, lazy } from 'react';
import { Section } from '@/types';
import { getBrandColor } from '@/config/brand-colors';
import { LazyMarkdownRenderer } from '@/components/ui/LazyMarkdownRenderer';
import { 
  HeroSectionProps, 
  ContactSectionProps, 
  ContactFormSectionProps,
  HumanResourcesSectionProps,
  FormSectionProps,
  BeyondStandardsSectionProps,
  DetailedSolutionsSectionProps,
  ServicesHeroSectionProps,
  ContactHeroSectionProps,
  ContactFAQSectionProps,
  CommitmentSectionProps,
  CommunitySectionProps,
  BenefitLogosCarouselSectionProps,
  ApplyNowSectionProps,
  VideoIntroSectionProps,
  ApplyVideoIntroSectionProps,
  ResponsiveBackgroundImage
} from '@/types/sections';
import { BenefitsPartnersVideoProps } from '@/components/sections/BenefitsPartnersVideo';

// Keep Hero loaded immediately as it's typically the first section (critical for LCP)
import Hero from '@/components/sections/Hero';

// Lazy load all other section components to reduce initial bundle size
const AboutHeroIntro = lazy(() => import('@/components/sections/AboutHeroIntro'));
const Contact = lazy(() => import('@/components/sections/Contact'));
const ContactForm = lazy(() => import('@/components/sections/ContactForm'));
const HumanResources = lazy(() => import('@/components/sections/HumanResources'));
const Form = lazy(() => import('@/components/sections/Form'));
const BeyondStandards = lazy(() => import('@/components/sections/BeyondStandards'));
const DetailedSolutions = lazy(() => import('@/components/sections/DetailedSolutions'));
const ServicesHero = lazy(() => import('@/components/sections/ServicesHero'));
const ContactHero = lazy(() => import('@/components/sections/ContactHero'));
const ContactFAQ = lazy(() => import('@/components/sections/ContactFAQ'));
const Community = lazy(() => import('@/components/sections/Community'));
const BenefitLogosCarousel = lazy(() => import('@/components/sections/BenefitLogosCarousel'));
const ApplyNow = lazy(() => import('@/components/sections/ApplyNow'));
const VideoIntro = lazy(() => import('@/components/sections/VideoIntro'));
const ApplyVideoIntro = lazy(() => import('@/components/sections/ApplyVideoIntro'));
const AboutOurStory = lazy(() => import('@/components/sections/about-our-story').then(m => ({ default: m.AboutOurStory })));
const AboutWhyPeru = lazy(() => import('@/components/sections/about-why-peru').then(m => ({ default: m.AboutWhyPeru })));
const AboutOurPeople = lazy(() => import('@/components/sections/about-our-people').then(m => ({ default: m.AboutOurPeople })));
const AboutCommitment = lazy(() => import('@/components/sections/about-commitment').then(m => ({ default: m.AboutCommitment })));
const AboutContactCta = lazy(() => import('@/components/sections/about-contact-cta').then(m => ({ default: m.AboutContactCta })));
const BenefitsCulture = lazy(() => import('@/components/sections/BenefitsCulture').then(m => ({ default: m.BenefitsCulture })));
const BenefitsPartnersVideo = lazy(() => import('@/components/sections/BenefitsPartnersVideo'));
const JoinUsSection = lazy(() => import('@/components/sections/JoinUsSection'));
const Spacer = lazy(() => import('@/components/sections/Spacer'));
const HealthcareServices = lazy(() => import('@/components/sections/HealthcareServices'));

// Loading fallback component
const SectionLoadingFallback = () => (
  <div className="min-h-[200px] flex items-center justify-center">
    <div className="animate-pulse text-gray-400">Loading section...</div>
  </div>
);

/**
 * Convert backgroundImage from string or object to ResponsiveBackgroundImage format
 */
function normalizeBackgroundImage(
  backgroundImage: string | ResponsiveBackgroundImage | undefined
): ResponsiveBackgroundImage | undefined {
  if (!backgroundImage) return undefined;
  
  // If it's already a ResponsiveBackgroundImage object, return it
  if (typeof backgroundImage === 'object' && ('mobile' in backgroundImage || 'desktop' in backgroundImage)) {
    return backgroundImage;
  }
  
  // If it's a string, convert to ResponsiveBackgroundImage with desktop property
  if (typeof backgroundImage === 'string') {
    return { desktop: backgroundImage };
  }
  
  return undefined;
}

function resolveBackgroundMedia(
  sectionData: Record<string, unknown>
): string | ResponsiveBackgroundImage | undefined {
  // Priority: backgroundMedia (unified) > backgroundVideo > backgroundImage
  return (
    (sectionData.backgroundMedia as string | ResponsiveBackgroundImage | undefined) ||
    (sectionData.backgroundVideo as string | ResponsiveBackgroundImage | undefined) ||
    (sectionData.backgroundImage as string | ResponsiveBackgroundImage | undefined)
  );
}

interface SectionComponentRendererProps {
  section: Section;
  isFirstSection?: boolean;
  sectionIndex?: number;
}

export default function SectionComponentRenderer({ section, isFirstSection = false, sectionIndex = 0 }: SectionComponentRendererProps) {
  if (!section || !section.isActive) {
    return null;
  }

  // Use hardcoded components based on type
  if (!section.type) {
    return null;
  }

  const sectionData = section.sectionData || {};
  const image = sectionData.image as string | undefined;
  const imageAlt = (sectionData.imageAlt as string) || 'Section image';

  // Handle SERVICES_HERO type sections specially
  if (section.type === 'SERVICES_HERO') {
    // Combine desktop and mobile fallback images into responsive object
    const servicesBackgroundMediaFallback = sectionData.backgroundMediaFallback as string | ResponsiveBackgroundImage | undefined;
    const servicesBackgroundMediaFallbackMobile = sectionData.backgroundMediaFallbackMobile as string | undefined;
    
    const servicesResponsiveFallbackImage = (() => {
      // If we have both desktop and mobile fallbacks, create responsive object
      if (servicesBackgroundMediaFallbackMobile) {
        const desktopFallback = typeof servicesBackgroundMediaFallback === 'string' 
          ? servicesBackgroundMediaFallback 
          : (typeof servicesBackgroundMediaFallback === 'object' && servicesBackgroundMediaFallback !== null && 'desktop' in servicesBackgroundMediaFallback)
            ? servicesBackgroundMediaFallback.desktop
            : undefined;
        
        return {
          desktop: desktopFallback || servicesBackgroundMediaFallbackMobile,
          mobile: servicesBackgroundMediaFallbackMobile,
        };
      }
      
      // Otherwise, return desktop fallback as-is (can be string or responsive object)
      return servicesBackgroundMediaFallback;
    })();
    
    const servicesHeroProps: ServicesHeroSectionProps = {
      title: sectionData.title as string | undefined,
      subtitle: sectionData.subtitle as string | undefined,
      highlight: sectionData.highlight as string | undefined,
      description: sectionData.description as string | undefined,
      backgroundImage: normalizeBackgroundImage(
        resolveBackgroundMedia(sectionData) as string | ResponsiveBackgroundImage | undefined
      ),
      backgroundVideo: sectionData.backgroundVideo as string | ResponsiveBackgroundImage | undefined,
      backgroundMediaFallback: servicesResponsiveFallbackImage,
      ctaText: sectionData.ctaText as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
      secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ServicesHero {...servicesHeroProps} />
      </Suspense>
    );
  }

  // Handle HERO type sections specially
  if (section.type === 'HERO') {
    // #region agent log
    const resolvedMedia = resolveBackgroundMedia(sectionData);
    const normalizedBg = normalizeBackgroundImage(resolvedMedia as string | ResponsiveBackgroundImage | undefined);
    const logData = {location:'SectionComponentRenderer.tsx:143',message:'Hero props - resolved media',data:{sectionKey:section.sectionKey,sectionId:section.id,sectionDataBackgroundMedia:sectionData.backgroundMedia,sectionDataBackgroundVideo:sectionData.backgroundVideo,resolvedMedia,normalizedBg},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
    console.log('[DEBUG]', logData);
    // #endregion
    // Combine desktop and mobile fallback images into responsive object
    const backgroundMediaFallback = sectionData.backgroundMediaFallback as string | ResponsiveBackgroundImage | undefined;
    const backgroundMediaFallbackMobile = sectionData.backgroundMediaFallbackMobile as string | undefined;
    
    const responsiveFallbackImage = (() => {
      // If we have both desktop and mobile fallbacks, create responsive object
      if (backgroundMediaFallbackMobile) {
        const desktopFallback = typeof backgroundMediaFallback === 'string' 
          ? backgroundMediaFallback 
          : (typeof backgroundMediaFallback === 'object' && backgroundMediaFallback !== null && 'desktop' in backgroundMediaFallback)
            ? backgroundMediaFallback.desktop
            : undefined;
        
        return {
          desktop: desktopFallback || backgroundMediaFallbackMobile,
          mobile: backgroundMediaFallbackMobile,
        };
      }
      
      // Otherwise, return desktop fallback as-is (can be string or responsive object)
      return backgroundMediaFallback;
    })();
    
    // Start with JSON props
    const heroProps: HeroSectionProps = {
      title: sectionData.title as string | undefined,
      subtitle: sectionData.subtitle as string | undefined,
      description: sectionData.description as string | undefined,
      backgroundImage: normalizedBg,
      backgroundVideo: sectionData.backgroundVideo as string | ResponsiveBackgroundImage | undefined,
      backgroundMediaFallback: responsiveFallbackImage,
      backgroundMediaFallbackMobile: backgroundMediaFallbackMobile,
      ctaText: sectionData.ctaText as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
      secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
    };


    return <Hero {...heroProps} />;
  }

  // Handle CONTACT type sections specially
  if (section.type === 'CONTACT') {
    // Start with JSON props
    const contactProps: ContactSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      address: sectionData.address as string | undefined,
      addressDescription: sectionData.addressDescription as string | undefined,
      email: sectionData.email as string | undefined,
      website: sectionData.website as string | undefined,
      mapEmbed: sectionData.mapEmbed as string | undefined,
      ctaText: sectionData.ctaText as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      ctaDescription: sectionData.ctaDescription as string | undefined,
    };


    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <Contact {...contactProps} />
      </Suspense>
    );
  }

  // Handle CONTACT_FORM type sections specially
  if (section.type === 'CONTACT_FORM') {
    const contactFormProps: ContactFormSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      location: sectionData.location as ContactFormSectionProps['location'],
      contactInfo: sectionData.contactInfo as ContactFormSectionProps['contactInfo'],
      cta: sectionData.cta as ContactFormSectionProps['cta'],
      media: sectionData.media as ContactFormSectionProps['media'],
      overlayTitle: sectionData.overlayTitle as string | undefined,
      overlaySubtitle: sectionData.overlaySubtitle as string | undefined,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ContactForm {...contactFormProps} />
      </Suspense>
    );
  }

  // Handle ABOUT type sections specially
  // Check if it's the "Where Talent Meets Opportunity" section (evoque-about-mdx)
  if (section.type === 'ABOUT') {
    // Use the new AboutHeroIntro component for the "Where Talent Meets Opportunity" section
    if (section.sectionKey === 'about' || section.id === 'evoque-about-mdx') {
      // #region agent log
      const resolvedMedia = resolveBackgroundMedia(sectionData);
      const normalizedBg = normalizeBackgroundImage(resolvedMedia as string | ResponsiveBackgroundImage | undefined);
      const logData = {location:'SectionComponentRenderer.tsx:210',message:'AboutHeroIntro props - resolved media',data:{sectionKey:section.sectionKey,sectionId:section.id,sectionDataBackgroundMedia:sectionData.backgroundMedia,resolvedMedia,normalizedBg},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'};
      console.log('[DEBUG]', logData);
      // #endregion
      const aboutHeroIntroProps = {
        title: sectionData.title as string | undefined,
        description: (sectionData.description as string) || undefined,
        backgroundImage: normalizedBg,
        backgroundMediaFallback: sectionData.backgroundMediaFallback as string | ResponsiveBackgroundImage | undefined,
        ctaText: sectionData.ctaText as string | undefined,
        ctaLink: sectionData.ctaLink as string | undefined,
        secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
        secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
        styles: section.styles as { textColor?: string },
      };
      
      return (
        <Suspense fallback={<SectionLoadingFallback />}>
          <AboutHeroIntro {...aboutHeroIntroProps} />
        </Suspense>
      );
    }
    
  }

  // Handle about page specific sections by sectionKey (check sectionKey first to avoid conflicts)
  // Note: about-hero now uses the HERO type with video support, so it falls through to the HERO handler below
  // if (section.sectionKey === 'about-hero') {
  //   return <AboutHero />;
  // }

  if (section.sectionKey === 'about-our-story' || section.type === 'STORY') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <AboutOurStory sectionData={sectionData as any} />
      </Suspense>
    );
  }

  if (section.sectionKey === 'about-why-peru') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <AboutWhyPeru sectionData={sectionData as any} />
      </Suspense>
    );
  }

  if (section.sectionKey === 'about-our-people' || section.type === 'PEOPLE') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <AboutOurPeople sectionData={sectionData as any} />
      </Suspense>
    );
  }

  if (section.sectionKey === 'about-commitment' || section.type === 'COMMITMENT') {
    const commitmentProps: CommitmentSectionProps = {
      title: sectionData.title as string | undefined,
      subtitle: sectionData.subtitle as string | undefined,
      description: sectionData.description as string | undefined,
      image: sectionData.image as string | undefined,
    };
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <AboutCommitment {...commitmentProps} />
      </Suspense>
    );
  }

  if (section.sectionKey === 'about-contact-cta' || section.type === 'CTA') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <AboutContactCta sectionData={sectionData as any} />
      </Suspense>
    );
  }

  // Handle JOIN_US type sections (fallback for neuroplastic rendering)
  // Note: join-us-form sections now use FORM type with formType='joinus' and are handled by Form component below
  if (section.type === 'JOIN_US') {
    const joinUsProps = {
      title: (sectionData.title as string) || undefined,
      description: (sectionData.description as string) || undefined,
      heroTitle: (sectionData.heroTitle as string) || undefined,
      heroDescription: (sectionData.heroDescription as string) || undefined,
    };
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <JoinUsSection {...joinUsProps} />
      </Suspense>
    );
  }

  // Handle benefits-culture section (now includes career section)
  if (section.sectionKey === 'benefits-culture' || section.type === 'BENEFITS_CULTURE') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <BenefitsCulture sectionData={sectionData as any} />
      </Suspense>
    );
  }

  // Handle benefits-career section (rendered within benefits-culture)
  if (section.sectionKey === 'benefits-career' || section.type === 'BENEFITS_CAREER') {
    // Return null since it's now part of benefits-culture
    return null;
  }

  // Handle benefits-partners-video section
  if (section.sectionKey === 'benefits-partners-video' || section.type === 'BENEFITS_PARTNERS_VIDEO') {
    const partnersVideoProps: BenefitsPartnersVideoProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      videoUrl: sectionData.videoUrl as string | undefined,
    };
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <BenefitsPartnersVideo {...partnersVideoProps} />
      </Suspense>
    );
  }


  // Handle HUMAN_RESOURCES type sections
  if (section.type === 'HUMAN_RESOURCES') {
    // Start with JSON props
    const hrProps: HumanResourcesSectionProps = {
      title: (sectionData.title as string) || '',
      description: (sectionData.description as string) || '',
      items: (sectionData.items as string[]) || [],
      // Support new component-based approach
      sectionComponents: sectionData.sectionComponents as HumanResourcesSectionProps['sectionComponents'],
      // CTA configuration
      ctaTitle: (sectionData.ctaTitle as string) || undefined,
      ctaDescription: (sectionData.ctaDescription as string) || undefined,
      ctaButtonText: (sectionData.ctaButtonText as string) || undefined,
      ctaButtonLink: (sectionData.ctaButtonLink as string) || undefined,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <HumanResources {...hrProps} />
      </Suspense>
    );
  }


  // Handle APPLY_VIDEO_INTRO type sections - dedicated client component so the
  // CTA can randomize between several Hireflix application URLs on each click.
  if (section.type === 'APPLY_VIDEO_INTRO') {
    const rawCtaLinks = sectionData.ctaLinks;
    const ctaLinks: string[] | undefined = Array.isArray(rawCtaLinks)
      ? (rawCtaLinks as unknown[]).filter((u): u is string => typeof u === 'string' && u.length > 0)
      : undefined;
    const applyVideoProps: ApplyVideoIntroSectionProps = {
      title: sectionData.title as string | undefined,
      subtitle: sectionData.subtitle as string | undefined,
      description: sectionData.description as string | undefined,
      videoUrl: sectionData.videoUrl as string | undefined,
      videoThumbnail: sectionData.videoThumbnail as string | undefined,
      ctaText: sectionData.ctaText as string | undefined,
      ctaIcon: sectionData.ctaIcon as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      ctaLinks: ctaLinks && ctaLinks.length > 0 ? ctaLinks : undefined,
      secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
      secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
    };
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ApplyVideoIntro {...applyVideoProps} />
      </Suspense>
    );
  }

  // Handle FORM type sections - all form pages should use the Form component with seeded forms
  if (section.type === 'FORM') {
    // Start with JSON props
    const formProps: FormSectionProps = {
      title: (sectionData.title as string) || undefined,
      description: (sectionData.description as string) || undefined,
      formType: (sectionData.formType as 'signin' | 'joinus' | 'contact') || 'signin',
      layout: (sectionData.layout as 'vertical' | 'horizontal') || 'vertical',
      heroTitle: (sectionData.heroTitle as string) || undefined,
      heroDescription: (sectionData.heroDescription as string) || undefined,
      formPosition: (sectionData.formPosition as 'left' | 'right') || 'right',
      heroMedia: sectionData.heroMedia as FormSectionProps['heroMedia'],
    };


    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <Form {...formProps} />
      </Suspense>
    );
  }

  // Handle BEYOND_STANDARDS type sections
  if (section.type === 'BEYOND_STANDARDS') {
    // Start with JSON props
    const beyondStandardsProps: BeyondStandardsSectionProps = {
      title: (sectionData.title as string) || undefined,
      description: (sectionData.description as string) || undefined,
      items: (sectionData.items as string[]) || [],
      backgroundImage: normalizeBackgroundImage(
        resolveBackgroundMedia(sectionData) as string | ResponsiveBackgroundImage | undefined
      ),
      backgroundVideo: sectionData.backgroundVideo as string | ResponsiveBackgroundImage | undefined,
      backgroundMediaFallback: sectionData.backgroundMediaFallback as string | undefined,
      ctaText: sectionData.ctaText as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
      secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
    };


    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <BeyondStandards {...beyondStandardsProps} />
      </Suspense>
    );
  }


  // Handle DETAILED_SOLUTIONS type sections
  if (section.type === 'DETAILED_SOLUTIONS') {
    const detailedSolutionsProps: DetailedSolutionsSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      solutions: (sectionData.solutions as DetailedSolutionsSectionProps['solutions']) || [],
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <DetailedSolutions {...detailedSolutionsProps} />
      </Suspense>
    );
  }


  // Handle CONTACT_HERO type sections
  if (section.type === 'CONTACT_HERO') {
    const contactHeroProps: ContactHeroSectionProps = {
      title: sectionData.title as string | undefined,
      highlight: sectionData.highlight as string | undefined,
      description: sectionData.description as string | undefined,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ContactHero {...contactHeroProps} />
      </Suspense>
    );
  }


  // Handle CONTACT_FAQ type sections
  if (section.type === 'CONTACT_FAQ') {
    const contactFAQProps: ContactFAQSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      faqs: (sectionData.faqs as ContactFAQSectionProps['faqs']) || [],
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ContactFAQ {...contactFAQProps} />
      </Suspense>
    );
  }

  // Handle COMMUNITY type sections
  if (section.type === 'COMMUNITY') {
    const communityProps: CommunitySectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      cards: (sectionData.cards as CommunitySectionProps['cards']) || [],
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <Community {...communityProps} />
      </Suspense>
    );
  }


  // Handle BENEFIT_LOGOS_CAROUSEL type sections
  if (section.type === 'BENEFIT_LOGOS_CAROUSEL') {
    const benefitLogosCarouselProps: BenefitLogosCarouselSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      logos: (sectionData.logos as BenefitLogosCarouselSectionProps['logos']) || [],
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <BenefitLogosCarousel {...benefitLogosCarouselProps} />
      </Suspense>
    );
  }

  // Handle APPLY_NOW type sections
  if (section.type === 'APPLY_NOW') {
    const applyNowProps: ApplyNowSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      ctaText: sectionData.ctaText as string | undefined,
      ctaLink: sectionData.ctaLink as string | undefined,
      secondaryCtaText: sectionData.secondaryCtaText as string | undefined,
      secondaryCtaLink: sectionData.secondaryCtaLink as string | undefined,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <ApplyNow {...applyNowProps} />
      </Suspense>
    );
  }

  // Handle VIDEO_INTRO type sections
  if (section.type === 'VIDEO_INTRO') {
    const videoIntroProps: VideoIntroSectionProps = {
      title: sectionData.title as string | undefined,
      description: sectionData.description as string | undefined,
      videoUrl: sectionData.videoUrl as string | undefined,
      videoThumbnail: sectionData.videoThumbnail as string | undefined,
      isFirstSection,
      sectionIndex,
    };

    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <VideoIntro {...videoIntroProps} />
      </Suspense>
    );
  }

  // Handle HEALTHCARE_SERVICES type sections
  if (section.type === 'HEALTHCARE_SERVICES') {
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <HealthcareServices
          title={sectionData.title as string | undefined}
          tagline={sectionData.tagline as string | undefined}
          subtitle={sectionData.subtitle as string | undefined}
          whyChooseUsTitle={sectionData.whyChooseUsTitle as string | undefined}
          whyChooseUsItems={(sectionData.whyChooseUsItems as string[]) || []}
          services={(sectionData.services as any[]) || []}
          whoWeServe={(sectionData.whoWeServe as string[]) || []}
          whyBilingualTitle={sectionData.whyBilingualTitle as string | undefined}
          whyBilingualDescription={sectionData.whyBilingualDescription as string | undefined}
          whyBilingualPoints={(sectionData.whyBilingualPoints as string[]) || []}
          keyBenefits={(sectionData.keyBenefits as any[]) || []}
        />
      </Suspense>
    );
  }

  // Handle SPACER type sections
  if (section.type === 'SPACER') {
    // Convert opacity to number if it's a string
    const opacityValue = sectionData.opacity;
    let opacity: number = 0.2; // Default opacity
    if (typeof opacityValue === 'number') {
      opacity = opacityValue;
    } else if (typeof opacityValue === 'string') {
      const parsed = parseFloat(opacityValue);
      if (!isNaN(parsed)) {
        opacity = parsed;
      }
    }

    const spacerProps = {
      height: (sectionData.height as string) || '1px',
      mobileHeight: (sectionData.mobileHeight as string) || undefined,
      backgroundColor: (sectionData.backgroundColor as string) || 'transparent',
      variant: (sectionData.variant as 'line' | 'space' | 'divider') || 'line',
      lineColor: (sectionData.lineColor as string) || undefined,
      lineWidth: (sectionData.lineWidth as string) || '100%',
      lineStyle: (sectionData.lineStyle as 'solid' | 'dashed' | 'dotted') || 'solid',
      opacity,
    };
    return (
      <Suspense fallback={<SectionLoadingFallback />}>
        <Spacer {...spacerProps} />
      </Suspense>
    );
  }

  // Render markdown content if available
  const content = sectionData.content as string | undefined;
  if (!content) {
    return null;
  }

  return (
    <section 
      data-section={section.sectionKey || section.id} 
      className="py-16 px-4" 
      style={{ backgroundColor: getBrandColor('white') }}
    >
      <div className="container mx-auto max-w-6xl">
        <div className={`grid ${image ? 'md:grid-cols-2' : ''} gap-12 items-center`}>
          {image && (
            <div className="relative w-full h-96 rounded-lg overflow-hidden">
              <Image
                src={image}
                alt={imageAlt}
                fill
                className="object-cover"
              />
            </div>
          )}
          <div>
            <div 
              className="prose prose-lg max-w-none"
              style={{
                color: '#4a4a4a'
              }}
            >
              <LazyMarkdownRenderer content={content} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
